/**
 * Matter Economics - shared constants & pure helpers (tag matter-economics).
 *
 * Label strings here are i18n KEYS (passed through `t()` at render time), not
 * user-facing text - see the locale files.
 */

import type { ActivityType, PricingModel } from '../../../types/economics';
import type { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';

/*
  THE PRICING VOCABULARY LIVES IN `shared/economics/pricingModel` NOW.

  A pricing model, a currency, a budget, a fixed fee and a contingency are not
  facts about matters - projects are priced with exactly the same five fields.
  The definitions moved to the shared module and are re-exported here under
  their original matter spellings, so every existing import keeps working.
*/
export {
  PRICING_MODELS,
  DEFAULT_PRICING_MODEL,
  PRICING_MODEL_LABEL_KEYS,
  requiresFixedFee,
  requiresContingency,
  emptyPricing as emptyMatterPricing,
  getPricingErrors as getMatterPricingErrors,
} from '../../shared/economics/pricingModel';
export type { PricingValue as MatterPricingValue } from '../../shared/economics/pricingModel';

/**
 * The matter's pricing model: the matter record's own, else the economics
 * rollup's, else hourly.
 *
 * The rollup is a fallback, not a second opinion - the matter record is what
 * the editor writes. It exists because a matter payload that does not carry
 * the pricing columns (an older /matters/details) used to make every
 * fixed-fee, hybrid and contingency matter read - and SAVE - as hourly.
 */
export const resolvePricingModel = (
  matterData: { pricing_model?: string | null } | null | undefined,
  economics?: { pricing_model?: string | null } | null
): PricingModel =>
  ((matterData?.pricing_model || economics?.pricing_model || 'hourly') as PricingModel);

// ── Time entries (US #2237) ───────────────────────────────────────────────────

/**
 * The 8 seeded activity types (MATTER_ECONOMICS_API.md). Used as a fallback when
 * the activity-types dropdown endpoint isn't reachable so the picker still works.
 * `label` here is an i18n key.
 */
export const ACTIVITY_TYPE_FALLBACK: ActivityType[] = [
  { id: 1, name: 'research', label: 'Research', sort_order: 10 },
  { id: 2, name: 'drafting', label: 'Drafting', sort_order: 20 },
  { id: 3, name: 'meeting', label: 'Meeting', sort_order: 30 },
  { id: 4, name: 'court', label: 'Court', sort_order: 40 },
  { id: 5, name: 'admin', label: 'Admin', sort_order: 50 },
  { id: 6, name: 'travel', label: 'Travel', sort_order: 60 },
  { id: 7, name: 'communication', label: 'Communication', sort_order: 70 },
  { id: 8, name: 'other', label: 'Other', sort_order: 80 },
];

/** Editable shape of a time entry (hours kept as a string for the input). */
export interface TimeEntryValue {
  entry_date: string;
  hours: string;
  activity_type_id: string;
  billable: boolean;
  description: string;
}

export const emptyTimeEntry = (entryDate: string): TimeEntryValue => ({
  entry_date: entryDate,
  hours: '',
  activity_type_id: '',
  billable: true,
  description: '',
});

/**
 * Validate a time entry: 0 < hours <= 24, a date is required and not in the
 * future. `today` is YYYY-MM-DD. Returns a map of field -> i18n error key.
 */
export const getTimeEntryErrors = (
  value: TimeEntryValue,
  today: string,
): Partial<Record<'entry_date' | 'hours', string>> => {
  const errors: Partial<Record<'entry_date' | 'hours', string>> = {};

  if (!value.entry_date) {
    errors.entry_date = 'Date is required';
  } else if (value.entry_date > today) {
    errors.entry_date = 'Date cannot be in the future';
  }

  const hours = Number(value.hours);
  if (value.hours.trim() === '') errors.hours = 'Hours are required';
  else if (Number.isNaN(hours)) errors.hours = 'Enter valid hours';
  else if (hours <= 0 || hours > 24) errors.hours = 'Hours must be between 0 and 24';

  return errors;
};

// ── Matter outcome (US #2240 / #2241) ─────────────────────────────────────────

/**
 * Badge colour per outcome-type code (#2288). Unknown/org-specific codes fall
 * back to gray via `outcomeBadgeColor`.
 */
export const OUTCOME_BADGE_COLORS: Record<string, BadgeColor> = {
  won: 'green',
  lost: 'red',
  settled: 'yellow',
  partial_settlement: 'yellow',
  dismissed: 'gray',
  withdrawn: 'gray',
  pending: 'gray',
};

/** Badge colour for an outcome-type code; gray for anything unmapped. */
export const outcomeBadgeColor = (name?: string | null): BadgeColor =>
  (name && OUTCOME_BADGE_COLORS[name]) || 'gray';
